import { Injectable } from '@angular/core';
import { AuthService } from '../../auth/auth.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ToasterService } from '../../providers/toaster/toaster.service';

export interface MenuPayload {
  menu_name: string;
  slug: string;
  parent_menu_id: number | null;
  url: string;
  type: string;
  sequence_no: number;
  is_active: number;
}

export interface UacActionPayload {
  main_menu_id: number;
  name: string;
  slug: string;
  action_url: string;
  is_active: number;
}
 
@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private apiUrl = environment?.apiUrl;
 
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toasterService: ToasterService,
  ) {}
 
  getHeader(): Observable<HttpHeaders> {
    const token = localStorage.getItem('token'); // or from service
 
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
 
    return of(headers); // ✅ MUST emit value
  }
 
  getMenus(): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        console.log('🚀 Headers received, calling API');
        return this.http.get(`${this.apiUrl}master/menus`, {
          headers,
          observe: 'response',
        }).pipe(
          tap((response) =>
            console.log('🚀 API response received', response.body),
          ),
        );
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }
 
  
 
  createMenu(data: MenuPayload): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/uac/main-menu/create`, data, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }

  getMenuById(id: number): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/uac/main-menu/${id}`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }
 
  updateMenu(id: number, data: MenuPayload): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/uac/main-menu/update/${id}`, data, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }
 
  deleteMenu(id: number): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/uac/main-menu/${id}/delete`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }

  deleteMainMenuSubmenu(id: number): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/uac/main-menu/${id}/delete`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }


  createUacAction(data: UacActionPayload): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/uac/action/create`, data, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }

  getUacActionById(id: number): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/uac/action/${id}`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }

  updateUacAction(id: number, data: UacActionPayload): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/uac/action/update/${id}`, data, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }

  deleteUacAction(id: number): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/uac/action/${id}/delete`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handleTokenExpired();
        }
        return throwError(() => error);
      }),
    );
  }
 
  private handleTokenExpired() {
    this.toasterService.setToaster(
      'default',
      'Session Expired',
      'The session has expired. Please log in again.',
    );
    this.authService.logout();
  }
}
